/**
 * The authored tail of the App Layer.
 *
 * Each record below is a whole app. There is no per-app code: `BlueprintApp`
 * renders all of them. Adding the next one (Budget Ask, Roadmap Cut, Board
 * Prep…) means adding a record here and a registry entry — nothing else.
 *
 * Method choices are deliberate and each is defended in `guidance.methodWhy`,
 * because the Agent shows that line to the owner at the moment they would
 * otherwise ask "why this one?".
 */

import type { AppBlueprint } from './types';

export const APP_BLUEPRINTS: AppBlueprint[] = [
  {
    appId: 'hire',
    toolId: 'simple-weighted-scoring',
    methodName: 'Weighted scoring',
    // Panelists score before they talk. Scoring in the debrief lets the loudest
    // voice anchor everyone else, which is the failure mode hiring panels have.
    timing: 'async',
    minimumCandidates: 2,
    capturesOwners: false,
    topFraction: 1,
    copy: {
      questionLabel: 'What role are you hiring for?',
      questionPlaceholder: 'e.g. Senior backend engineer, platform team',
      candidateNoun: 'candidate',
      candidateNounPlural: 'candidates',
      candidateLabel: 'Who is in the running?',
      candidatePlaceholder: 'One candidate per line, e.g.\nPriya R.\nMarcus T.\nJordan K.',
      candidateHint:
        'Everyone on the panel scores independently against the same criteria, before the debrief.',
      resultHeading: 'How the panel scored them',
      topLabel: 'Ranked',
      restLabel: '',
    },
    criteria: {
      label: 'What matters for this role?',
      hint: 'The panel scores each candidate from 1 to 5 on every line, and higher is better. Change, remove or add lines to fit the role.',
      suggested: [
        'Skills for the role',
        'Relevant experience',
        'Works well with the team',
        'Room to grow',
      ],
    },
    guidance: {
      methodWhy:
        'Weighted scoring makes the panel agree on what matters before they argue about who is best — and because everyone scores on their own, the debrief starts from independent judgement instead of whoever spoke first.',
      resultAdvice:
        'This is the panel’s read, not the decision. Use it to focus the debrief on where scores actually disagree.',
    },
  },
  {
    appId: 'vendor-select',
    toolId: 'simple-weighted-scoring',
    methodName: 'Weighted scoring',
    timing: 'async',
    minimumCandidates: 2,
    capturesOwners: false,
    topFraction: 1,
    copy: {
      questionLabel: 'What are you buying?',
      questionPlaceholder: 'e.g. Customer support platform for FY26',
      candidateNoun: 'vendor',
      candidateNounPlural: 'vendors',
      candidateLabel: 'Which vendors are you comparing?',
      candidatePlaceholder: 'One vendor per line, e.g.\nZendesk\nIntercom\nFront',
      candidateHint:
        'Score against agreed criteria so the choice survives the procurement conversation.',
      resultHeading: 'How the vendors scored',
      topLabel: 'Ranked',
      restLabel: '',
    },
    criteria: {
      label: 'What matters in this purchase?',
      hint: 'Everyone scores each vendor from 1 to 5 on every line, and higher is better. Change, remove or add lines to fit what you need.',
      // Worded so a higher score is better: "Value for the cost" and "Low risk",
      // not "Cost" and "Risk", which would reward the expensive, risky option.
      suggested: ['Fit to the need', 'Value for the cost', 'Reliability and support', 'Low risk'],
    },
    guidance: {
      methodWhy:
        'A vendor choice gets audited later. Weighted scoring leaves a written trail of what you valued and how each option measured up, which is exactly what finance and legal ask for.',
      resultAdvice:
        'Publish this with the scores intact — it is the backing for the purchase recommendation.',
    },
  },
  {
    appId: 'risk-triage',
    toolId: 'impact-effort-matrix',
    methodName: 'Impact vs effort to mitigate',
    timing: 'async',
    minimumCandidates: 3,
    capturesOwners: true,
    // Triage is explicitly about drawing a line: some risks get worked, the
    // rest get watched. Showing only the top half is the whole point.
    topFraction: 0.5,
    copy: {
      questionLabel: 'What are you triaging risk for?',
      questionPlaceholder: 'e.g. Q3 platform migration',
      candidateNoun: 'risk',
      candidateNounPlural: 'risks',
      candidateLabel: 'What could go wrong?',
      candidatePlaceholder:
        'One risk per line, e.g.\nData migration exceeds the maintenance window\nKey vendor contract lapses mid-cutover\nOn-call coverage gaps during the switch',
      candidateHint:
        'Name them plainly. A risk nobody wrote down is a risk nobody owns.',
      resultHeading: 'Where the risk actually is',
      topLabel: 'Mitigate now',
      restLabel: 'Watch',
    },
    guidance: {
      methodWhy:
        'Plotting impact against effort-to-mitigate finds the risks that are both serious and cheap to defuse — the ones worth doing this week rather than debating all quarter.',
      resultAdvice:
        'Give every risk in the top group an owner. An unowned risk is a risk that will surprise you.',
    },
  },
  {
    appId: 'post-mortem',
    toolId: 'dot-voting',
    methodName: 'Dot voting',
    // A post-mortem is a room. Sync keeps it a conversation, not a survey.
    timing: 'sync',
    minimumCandidates: 3,
    capturesOwners: true,
    topFraction: 0.5,
    copy: {
      questionLabel: 'What are you reviewing?',
      questionPlaceholder: 'e.g. The March 14 outage',
      candidateNoun: 'factor',
      candidateNounPlural: 'factors',
      candidateLabel: 'What contributed?',
      candidatePlaceholder:
        'One factor per line, e.g.\nAlerting did not page the right team\nRollback took 40 minutes\nRunbook was out of date',
      candidateHint:
        'Collect everything first, judge after. Sorting while you gather suppresses the uncomfortable ones.',
      resultHeading: 'What the room says mattered most',
      topLabel: 'Fix these',
      restLabel: 'Noted',
    },
    guidance: {
      methodWhy:
        'Dot voting surfaces what the room genuinely agrees mattered, fast, without the seniority gradient that makes people defer in an open discussion.',
      resultAdvice:
        'Assign owners now, in the room. Post-mortems fail at the follow-through, not the analysis.',
    },
  },
  {
    appId: 'ask-the-room',
    toolId: 'dot-voting',
    methodName: 'Dot voting',
    timing: 'sync',
    minimumCandidates: 2,
    capturesOwners: false,
    topFraction: 1,
    copy: {
      questionLabel: 'What are you asking?',
      questionPlaceholder: 'e.g. Which of these should we do first?',
      candidateNoun: 'option',
      candidateNounPlural: 'options',
      candidateLabel: 'What are the options?',
      candidatePlaceholder: 'One option per line, e.g.\nShip the beta\nHold for the audit\nCut scope and ship Friday',
      candidateHint: 'Two minutes of setup. Everyone answers from their phone.',
      resultHeading: 'Where the room landed',
      topLabel: 'Ranked',
      restLabel: '',
    },
    guidance: {
      methodWhy:
        'The fastest honest read on a room. Dots are anonymous, so you get what people think rather than what they think the most senior person wants to hear.',
      resultAdvice:
        'A read, not a mandate. If the room is split, that split is the useful finding.',
    },
  },
];

export function getBlueprint(appId: string): AppBlueprint | undefined {
  return APP_BLUEPRINTS.find((b) => b.appId === appId);
}
